<template>
  <div class="pin-input-container">
    <div class="pin-input">
      <input
        v-for="(digit, index) in digits"
        :key="index"
        :ref="el => { if (el) inputs[index] = el }"
        :value="getDisplayValue(digit)"
        type="text"
        inputmode="numeric"
        maxlength="1"
        class="pin-digit"
        @input="handleInput(index, $event)"
        @keydown="handleKeydown(index, $event)"
        @paste="handlePaste($event)"
      />
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted } from 'vue'
import { useHaptics } from '../../composables/useHaptics.js'

export default {
  name: 'PinInput',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    length: {
      type: Number,
      default: 4
    }
  },
  emits: ['update:modelValue', 'complete'],
  setup(props, { emit }) {
    const digits = ref(Array(props.length).fill(''))
    const inputs = ref([])
    const { light, success } = useHaptics()

    const getDisplayValue = (digit) => {
      // Отображаем точку вместо цифры
      return digit ? '•' : ''
    }

    const updateModelValue = () => {
      const pin = digits.value.join('')
      emit('update:modelValue', pin)
      if (pin.length === props.length) {
        // Вибрация успеха при полном заполнении PIN
        success()
        emit('complete', pin)
      }
    }

    const handleInput = (index, event) => {
      const value = event.target.value.replace(/\D/g, '').slice(0, 1)
      digits.value[index] = value

      // Легкая вибрация при вводе каждой цифры
      if (value) {
        light()
      }

      if (value && index < props.length - 1) {
        inputs.value[index + 1]?.focus()
      }

      updateModelValue()
    }

    const handleKeydown = (index, event) => {
      if (event.key === 'Backspace' && !digits.value[index] && index > 0) {
        // Легкая вибрация при удалении
        light()
        inputs.value[index - 1]?.focus()
        digits.value[index - 1] = ''
        updateModelValue()
      }
    }

    const handlePaste = (event) => {
      event.preventDefault()
      const pastedData = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, props.length)
      
      for (let i = 0; i < props.length; i++) {
        digits.value[i] = pastedData[i] || ''
      }
      
      const filledIndex = Math.min(pastedData.length, props.length - 1)
      inputs.value[filledIndex]?.focus()
      updateModelValue()
    }

    watch(() => props.modelValue, (newValue) => {
      if (newValue !== digits.value.join('')) {
        const newDigits = newValue.split('').slice(0, props.length)
        digits.value = [...newDigits, ...Array(props.length - newDigits.length).fill('')]
      }
    })

    onMounted(() => {
      inputs.value[0]?.focus()
    })

    return {
      digits,
      inputs,
      getDisplayValue,
      handleInput,
      handleKeydown,
      handlePaste
    }
  }
}
</script>

<style scoped>
.pin-input-container {
  display: flex;
  justify-content: center;
  width: 100%;
}

.pin-input {
  display: flex;
  gap: var(--spacing-md);
  justify-content: center;
}

.pin-digit {
  width: 56px;
  height: 56px;
  text-align: center;
  font-size: var(--font-size-2xl);
  font-weight: 600;
  background: var(--color-bg-secondary);
  border: 2px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  outline: none;
  transition: all 0.2s ease;
}

.pin-digit:focus {
  border-color: var(--color-accent);
  background: var(--color-bg-card);
}

@media (max-width: 480px) {
  .pin-digit {
    width: 48px;
    height: 48px;
    font-size: var(--font-size-xl);
  }
  
  .pin-input {
    gap: var(--spacing-sm);
  }
}
</style>

